// ./src/components/lists/ListCalled/Item/index.tsx
import React, { useState, useMemo, useCallback, useEffect } from 'react';

import { FiArrowDown, FiCornerUpRight } from 'react-icons/fi';
import {
  IoEllipsisHorizontal,
  IoCheckmarkCircle,
  IoRemoveCircle,
  IoFlag,
  IoAlarm,
  IoConstruct,
  IoTrash,
} from 'react-icons/io5';

import { ButtonMain, InputRadio, Paragraph } from '@components/index';
import {
  ConfigRoutes,
  ConfigStyles,
  ConfigTransition,
  ConfigValues,
} from '@config/index';
import Called from '@models/Called';
import { ISelect } from '@pages/private/Called/List/typing';
import apiZapinho from '@services/apis/apiZapinho';
import { apiRebox } from '@services/index';
import { formatDate, formatText } from '@utils/formatters';
import { toastify } from '@utils/notifiers';

import {
  Container,
  CompositeItem,
  CompositeItemText,
  Dropdown,
  DropdownItem,
  AssistantsContainer,
  AssistantLabel,
} from './styles';

interface IProps {
  called: Called;
}

const ListCalledItem: React.FC<IProps> = ({ called }) => {
  const [assistants, setAssistants] = useState<ISelect[]>([]);
  const [assistantsExpanded, setAssistantsExpanded] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const requestedIn = useMemo(() => {
    const date = called.date_created;
    return formatDate.addMask(date);
  }, []);

  const serviceFor = useMemo(() => {
    const date = called.appointment_date || called.date_created;
    return formatDate.addMask(date);
  }, []);

  const requestedAssistant = useMemo(() => {
    const name = called.who_is_requesting?.name;
    const splittedName = name?.split(' ');
    if (
      called.who_is_requesting?.role !== 'admin' &&
      called.who_is_requesting?.role !== 'assistant' &&
      called.who_is_requesting?.role !== 'supervisor'
    ) {
      return 'Solicitado pelo cliente.';
    }
    return `${formatText.capitalizedFirstLetter(
      splittedName?.[0],
    )} ${formatText.capitalizedFirstLetter(splittedName?.[1])}`;
  }, []);

  const getAssistants = useCallback(async () => {
    try {
      const { data: reboxAssistantUsers } = await apiRebox.get(
        `/users?per_page=50&role=assistant`,
      );
      const { data: reboxSupervisorUsers } = await apiRebox.get(
        `/users?per_page=50&role=supervisor`,
      );
      const { data: zapinhoUsers } = await apiZapinho.get('');

      const assistantUsers: any[] = [
        ...reboxAssistantUsers.data,
        ...reboxSupervisorUsers.data,
      ];
      const assistantsUpdated: ISelect[] = [];

      assistantUsers.forEach(element => {
        const zapinhoUser = zapinhoUsers.find(
          (user: any) => user.email === element.email,
        );
        if (zapinhoUser && zapinhoUser.status === 'ONLINE') {
          assistantsUpdated.push({
            label: element.name,
            value: element.id,
          });
        }
      });

      setAssistants(assistantsUpdated);
    } catch (error) {
      toastify(`Houve um error ao trocar de assistente.`, 'error');
    }
  }, []);

  const [selected_assistant_id, setSelectedAssistantId] = useState<
    string | null
  >(null);

  const changeAssistant = useCallback(async () => {
    try {
      if (!selected_assistant_id) {
        return;
      }
      const called_to_update = called;

      called_to_update.who_is_requesting_id = selected_assistant_id;
      const { data: response } = await apiRebox.put(
        `/called/${called.id}`,
        called_to_update,
      );

      toastify(`Atendente alterado com sucesso!.`, 'success');
    } catch (error: any) {
      toastify(`Houve um erro ao trocar de assistente.`, 'error');
    } finally {
      setAssistantsExpanded(false);
      setIsExpanded(false);
    }
  }, [selected_assistant_id]);

  useEffect(() => {
    getAssistants();
  }, []);

  return (
    <Container
      className={
        called.status === ConfigValues.rebox.called.status.open ? 'cheerUp' : ''
      }
      onClick={() => setIsExpanded(prev => !prev)}
    >
      <Paragraph nameColor="black" textAlign="start" fontSize={12}>
        {requestedIn}
      </Paragraph>
      <Paragraph nameColor="black" textAlign="start" fontSize={12}>
        {serviceFor}
      </Paragraph>
      <CompositeItem>
        {ConfigValues.rebox.called.status.open === called.status && (
          <>
            <IoFlag size={12} color={ConfigStyles.rebox.colors.red.main} />
            <CompositeItemText
              style={{ color: ConfigStyles.rebox.colors.red.main }}
            >
              {
                ConfigTransition.rebox_called_status[
                  called.status?.toLowerCase() || 'undefined'
                ]
              }
            </CompositeItemText>
          </>
        )}

        {ConfigValues.rebox.called.status.in_progress === called.status && (
          <>
            <IoAlarm
              size={12}
              color={ConfigStyles.rebox.colors.yellowSunFlower.main}
            />
            <CompositeItemText
              style={{ color: ConfigStyles.rebox.colors.yellowSunFlower.main }}
            >
              {
                ConfigTransition.rebox_called_status[
                  called.status?.toLowerCase() || 'undefined'
                ]
              }
            </CompositeItemText>
          </>
        )}

        {ConfigValues.rebox.called.status.in_attendance === called.status && (
          <>
            <IoConstruct
              size={12}
              color={ConfigStyles.rebox.colors.lightBlue.main}
            />
            <CompositeItemText
              style={{ color: ConfigStyles.rebox.colors.lightBlue.main }}
            >
              {
                ConfigTransition.rebox_called_status[
                  called.status?.toLowerCase() || 'undefined'
                ]
              }
            </CompositeItemText>
          </>
        )}
        {ConfigValues.rebox.called.status.done === called.status && (
          <>
            <IoCheckmarkCircle
              size={12}
              color={ConfigStyles.rebox.colors.greenEmerald.main}
            />
            <CompositeItemText
              style={{ color: ConfigStyles.rebox.colors.greenEmerald.main }}
            >
              {
                ConfigTransition.rebox_called_status[
                  called.status?.toLowerCase() || 'undefined'
                ]
              }
            </CompositeItemText>
          </>
        )}

        {ConfigValues.rebox.called.status.canceled === called.status && (
          <>
            <IoRemoveCircle
              size={12}
              color={ConfigStyles.rebox.colors.gray.main}
            />
            <CompositeItemText
              style={{ color: ConfigStyles.rebox.colors.gray.main }}
            >
              {
                ConfigTransition.rebox_called_status[
                  called.status?.toLowerCase() || 'undefined'
                ]
              }
            </CompositeItemText>
          </>
        )}

        {ConfigValues.rebox.called.status.deleted === called.status && (
          <>
            <IoTrash
              size={12}
              color={ConfigStyles.rebox.colors.gray.opacity.average}
            />
            <CompositeItemText
              style={{ color: ConfigStyles.rebox.colors.gray.opacity.average }}
            >
              {
                ConfigTransition.rebox_called_status[
                  called.status?.toLowerCase() || 'undefined'
                ]
              }
            </CompositeItemText>
          </>
        )}
      </CompositeItem>
      <Paragraph nameColor="black" textAlign="start" fontSize={12}>
        {called.service?.name.toUpperCase()}
      </Paragraph>
      <Paragraph nameColor="black" textAlign="start" fontSize={12}>
        {called.customer?.cpf || called.customer?.cnpj}
      </Paragraph>
      <Paragraph nameColor="black" textAlign="start" fontSize={12}>
        {/* {`${called.vehicle?.brand.toUpperCase()} ${called.vehicle?.model.toUpperCase()} ${called.vehicle?.license_plate.toUpperCase()}`} */}
        {called.vehicle?.license_plate.toUpperCase()}
      </Paragraph>

      <Paragraph nameColor="black" textAlign="start" fontSize={12}>
        {
          ConfigTransition.rebox_called_vehicle_situation[
            called.vehicle_situation?.toLowerCase() || 'undefined'
          ]
        }
      </Paragraph>

      <Paragraph nameColor="black" textAlign="start" fontSize={12}>
        {
          ConfigTransition.rebox_called_location_type[
            called.location_type?.toLowerCase() || 'undefined'
          ]
        }
      </Paragraph>
      <Paragraph nameColor="black" textAlign="start" fontSize={12}>
        {requestedAssistant}
      </Paragraph>

      {/* <Paragraph nameColor="black" textAlign="start" fontSize={12}>
        {called.source_address?.full_address}
      </Paragraph>

      <Paragraph nameColor="black" textAlign="start" fontSize={12}>
        {called.destination_address?.full_address}
      </Paragraph> */}

      {/* <Paragraph nameColor="black" textAlign="start" fontSize={12}>
        {called.distance_between_points_in_km?.toFixed(1)}
      </Paragraph> */}

      <Paragraph nameColor="black" className="btn-options">
        <IoEllipsisHorizontal
          size={16}
          color={ConfigStyles.rebox.colors.black.main}
          style={{ cursor: 'pointer' }}
        />
      </Paragraph>
      <Dropdown
        expande={isExpanded}
        onClick={e => {
          e.stopPropagation();
        }}
      >
        <DropdownItem
          to={`${ConfigRoutes.rebox.privates.called.next.drives.path}/${called.id}`}
        >
          <FiCornerUpRight
            size={16}
            title="ícone de setas"
            color={ConfigStyles.rebox.colors.black.main}
          />
          <Paragraph
            nameColor="black"
            textAlign="start"
            fontSize={11}
            style={{
              marginLeft: '1vw',
              fontWeight: 500,
            }}
          >
            Abrir
          </Paragraph>
        </DropdownItem>

        <DropdownItem
          to={'#'}
          onClick={e => {
            e.preventDefault();
            setAssistantsExpanded(prev => !prev);
          }}
        >
          {assistantsExpanded ? (
            <FiArrowDown />
          ) : (
            <FiCornerUpRight
              size={16}
              title="ícone de setas"
              color={ConfigStyles.rebox.colors.black.main}
            />
          )}
          <Paragraph
            nameColor="black"
            textAlign="start"
            fontSize={11}
            style={{
              marginLeft: '1vw',
              fontWeight: 500,
            }}
          >
            Transferir
          </Paragraph>
        </DropdownItem>

        {assistantsExpanded && (
          <Paragraph
            textAlign="start"
            style={{
              marginLeft: '1vw',
              fontWeight: 500,
            }}
          >
            Escolha o atendente
          </Paragraph>
        )}
        <AssistantsContainer expanded={assistantsExpanded}>
          <InputRadio
            onChange={e => setSelectedAssistantId(e.target.value)}
            options={assistants}
            row={true}
          />
        </AssistantsContainer>
        {assistantsExpanded && (
          <div style={{ padding: 10 }}>
            <ButtonMain onClick={() => changeAssistant()}>
              Mudar atendente
            </ButtonMain>
          </div>
        )}
      </Dropdown>
    </Container>
  );
};

export default ListCalledItem;
